import { apolloClient } from 'common/graphql/clients';
import webView from 'common/webView';

import { MachineServices } from '../../../../../common/bento/types/MachineConfig';
import { TrackSegmentEventDocument } from '../../../../Analytics/graphql/mutations/trackSegmentEvent.gql';
import { OnboardingCompanyDocument } from '../../../graphql/queries/onboardingCompany.gql';
import { UpdateFinishedProfileSetupDocument } from '../../standard/graphql/mutations/updateFinishedProfileSetup.gql';
import { AbortSasuCreationDocument } from '../graphql/mutations/abortSasuCreation.gql';
import { SubmitCapitalDepositApplicationDocument } from '../graphql/mutations/submitCapitalDepositApplication.gql';
import { CheckForSasuInfoSetDocument } from '../graphql/queries/checkForSasuInfoSet.gql';
import { Model } from './model';

export enum Service {
  FinishProfileSetup = 'finishProfileSetup',
  SubmitApplication = 'submitApplication',
  CheckForSasuInfoSet = 'checkForSasuInfoSet',
  AbortSasuCreation = 'abortSasuCreation',
}

const services: MachineServices<Model, Service> = {
  /**
   * In case the user selects an activity we don’t support in the Landbot, they are redirected to a
   * legal tech partner. We need to abort the company creation application, so when they come back
   * they can start a regular capital deposit application.
   */
  abortSasuCreation: async ({ getModuleData }) => {
    const { company } = getModuleData().viewer;

    // Will trigger the `applicationAborted` subscription which will clear the company context
    await apolloClient.mutate({
      mutation: AbortSasuCreationDocument,
      variables: {
        input: {
          id: company.companyProfileId,
        },
      },
    });
  },
  /**
   * In case we don’t receive the sasu info set event after the delay, we will force a refetch
   * to manually check if the landbot has been processed (= legal name is filled).
   * To avoid showing an error when we "missed" the event.
   */
  checkForSasuInfoSet: async ({ getModuleData }) => {
    const { data } = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: CheckForSasuInfoSetDocument,
      variables: {
        companyProfileId: getModuleData().viewer.company.companyProfileId,
      },
    });

    if (!data.viewer.company.profile.legalName) {
      throw new Error(
        'Company creation SASU information not set after timeout reached',
      );
    }
  },
  finishProfileSetup: async ({ getModuleData }) => {
    const { viewer } = getModuleData();

    if (!viewer.company) {
      throw new Error(`Can't call finishProfileSetup without a company`);
    }

    const { company } = viewer;

    /**
     * We don't await this tracking event.
     */
    apolloClient.mutate({
      mutation: TrackSegmentEventDocument,
      variables: {
        input: {
          companyProfileId: company.companyProfileId,
          event: 'Applicant Profile Completed',
          properties: {
            accountType: 'sasu-creation',
            isMicro: false,
            isMultiKYC: false,
            legalForm: company.profile.legalForm,
            withWebView: webView.isInWebView,
          },
        },
      },
    });

    return apolloClient.mutate({
      mutation: UpdateFinishedProfileSetupDocument,
      refetchQueries: [
        {
          query: OnboardingCompanyDocument,
          variables: { companyProfileId: company.companyProfileId },
        },
      ],
      variables: {
        metadata: {
          hasFinishedProfileSetup: true,
        },
      },
    });
  },

  submitApplication: ({ getModuleData }) => {
    const { company } = getModuleData().viewer;

    if (!company?.capitalDeposit) {
      throw new Error(`capitalDeposit required in submitApplication`);
    }

    return apolloClient.mutate({
      mutation: SubmitCapitalDepositApplicationDocument,
      refetchQueries: [
        {
          query: OnboardingCompanyDocument,
          variables: { companyProfileId: company.companyProfileId },
        },
      ],
      variables: { capitalDepositId: company.capitalDeposit.capitalDepositId },
    });
  },
};

export default services;
